import { createPortal } from "react-dom";
import { useEffect, useRef, useState } from "react";
import { Dropdown, Modal } from "react-bootstrap";
import { HiXMark } from "react-icons/hi2";
import { motion, AnimatePresence } from "framer-motion";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import axios from "axios";
import { baseurl } from "../BaseUrl/Baseurl";
import { useSelector } from "react-redux";
import { TbFileExport } from "react-icons/tb";
import { HiOutlineDownload } from "react-icons/hi";
import CustomSuccessToastContainer from "../Toast/SuccessToast";
import CustomErrorToastContainer from "../Toast/ErrorToast";
import { ThreeDots } from "react-loader-spinner";
import { GrFormNext, GrFormPrevious } from "react-icons/gr";
import ReactPaginate from "react-paginate";
import { RiFilter2Fill } from "react-icons/ri";
import LogoutComponent from "../../Pages/Logout";
import "./master.css";

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

// Filter hierarchy, top to bottom.
const FILTER_ORDER = [
  "wing",
  "circle",
  "division",
  "sub_division",
  "area",
  "project_name",
];

// Key used in the project-details request body for each level.
const ID_KEY = {
  wing: "wingId",
  circle: "circleId",
  division: "divisionId",
  sub_division: "subDivisionId",
  area: "areaId",
};

// Which child list to load after a level is picked.
const CHILD_LIST = {
  wing: { field: "circle", param: "wingId" },
  circle: { field: "division", param: "circleId" },
  division: { field: "subDivision", param: "divisionId" },
  sub_division: { field: "area", param: "subDivisionId" },
};

const readSession = (key) => {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(key) || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

// useState for an array that is mirrored into sessionStorage.
function useSessionArray(key) {
  const [value, setValue] = useState(() => readSession(key));
  useEffect(() => {
    sessionStorage.setItem(key, JSON.stringify(value));
  }, [key, value]);
  return [value, setValue];
}

function useMediaQuery(query) {
  const [matches, setMatches] = useState(
    () => typeof window !== "undefined" && window.matchMedia(query).matches,
  );
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);
    setMatches(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);
  return matches;
}

const SearchIcon = () => (
  <svg
    width="17"
    height="16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    aria-labelledby="search"
  >
    <path
      d="M7.667 12.667A5.333 5.333 0 107.667 2a5.333 5.333 0 000 10.667zM14.334 14l-2.9-2.9"
      stroke="currentColor"
      strokeWidth="1.333"
      strokeLinecap="round"
      strokeLinejoin="round"
    ></path>
  </svg>
);

const ClearIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-6 w-6"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M6 18L18 6M6 6l12 12"
    ></path>
  </svg>
);

const ExcelIcon = ({ fill = "#fff", size = 20 }) => (
  <svg
    fill={fill}
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 50 50"
    aria-label="Excel file icon"
  >
    <path d="M28.8125 .03125L.8125 5.34375C.339844 5.433594 0 5.863281 0 6.34375L0 43.65625C0 44.136719 .339844 44.566406 .8125 44.65625L28.8125 49.96875C28.875 49.980469 28.9375 50 29 50C29.230469 50 29.445313 49.929688 29.625 49.78125C29.855469 49.589844 30 49.296875 30 49L30 1C30 .703125 29.855469 .410156 29.625 .21875C29.394531 .0273438 29.105469 -.0234375 28.8125 .03125ZM32 6L32 13L34 13L34 15L32 15L32 20L34 20L34 22L32 22L32 27L34 27L34 29L32 29L32 35L34 35L34 37L32 37L32 44L47 44C48.101563 44 49 43.101563 49 42L49 8C49 6.898438 48.101563 6 47 6ZM36 13L44 13L44 15L36 15ZM6.6875 15.6875L11.8125 15.6875L14.5 21.28125C14.710938 21.722656 14.898438 22.265625 15.0625 22.875L15.09375 22.875C15.199219 22.511719 15.402344 21.941406 15.6875 21.21875L18.65625 15.6875L23.34375 15.6875L17.75 24.9375L23.5 34.375L18.53125 34.375L15.28125 28.28125C15.160156 28.054688 15.035156 27.636719 14.90625 27.03125L14.875 27.03125C14.8125 27.316406 14.664063 27.761719 14.4375 28.34375L11.1875 34.375L6.1875 34.375L12.15625 25.03125ZM36 20L44 20L44 22L36 22ZM36 27L44 27L44 29L36 29ZM36 35L44 35L44 37L36 37Z"></path>
  </svg>
);

/* -------------------------------------------------------------------------- */
/* One filter dropdown (replaces the six copy-pasted blocks, x2)               */
/* -------------------------------------------------------------------------- */

function FilterSelect({
  field,
  isOpen,
  search,
  onToggle,
  onSearch,
  onSelect,
  dropUp,
}) {
  const { key, label, list, selected, nameKey, disabled, locked } = field;
  const isDisabled = !!disabled || !!locked;
  const query = (search || "").toLowerCase();
  const visible = (list || []).filter(
    (item) =>
      item &&
      typeof item[nameKey] === "string" &&
      item[nameKey].toLowerCase().includes(query),
  );

  const menu = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: dropUp ? 10 : -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: dropUp ? 10 : -10 }}
          transition={{ duration: 0.2 }}
          className={`bg-white border rounded-md mt-1 w-full ${
            dropUp ? "zbottom" : "zelement"
          }`}
        >
          <form className="form-in w-100" onSubmit={(e) => e.preventDefault()} autoComplete="off">
            <button type="button" className="mb-2" tabIndex={-1}>
              <SearchIcon />
            </button>
            <input
              className="input"
              placeholder="Search"
              type="text"
              value={search || ""}
              onChange={(e) => onSearch(key, e.target.value)}
              autoComplete="off"
            />
            <button
              className="reset"
              type="button"
              aria-label="Clear search"
              onClick={() => onSearch(key, "")}
            >
              <ClearIcon />
            </button>
          </form>

          <div className="scrollbar xyz w-100">
            {visible.length > 0 ? (
              visible.map((item, index) => (
                <button
                  key={item.id ?? item[nameKey] ?? index}
                  className="px-3 py-2 border-bottom clickable-element btn w-100 text-left"
                  type="button"
                  onClick={() => onSelect(key, item)}
                >
                  {item[nameKey]}
                </button>
              ))
            ) : (
              <div className="px-3 py-2">No results found</div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <div className={`mt-2  filter-field${dropUp ? " rel" : ""}`}>
      <label>{label}</label>
      {dropUp && menu}
      <button
        type="button"
        aria-expanded={isOpen}
        aria-disabled={isDisabled}
        className={`form-control border rounded-md px-3 py-2 bg-white d-flex align-items-center justify-content-between relative-element${
          isDisabled ? " disabled-dropdown" : ""
        }`}
        onClick={() => onToggle(key)}
      >
        <span className="flex-area">
          {selected.length > 0 ? (
            <div className="d-flex gap-1 align-items-center rounded-pill px-2 py-1 justify-content-center font12">
              <span className="p-0 text-dark">{selected[0][nameKey]}</span>
            </div>
          ) : (
            <span className="text-dark">Select</span>
          )}
        </span>
        <span className="d-flex m-0 justify-content-end">
          {isOpen ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
        </span>
      </button>
      {!dropUp && menu}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Page                                                                       */
/* -------------------------------------------------------------------------- */

export default function MasterTab() {
  const userData = useSelector((state) => state.userDetails);
  const isDE = userData?.role === "DE";

  const isPhone = useMediaQuery("(max-width: 767.98px)");
  const isDesktop = useMediaQuery("(min-width: 992px)");

  // ---- table + lists (persisted per session) ----
  const [masterTab1, setMasterTab1] = useSessionArray("masterTab1Data");
  const [WingList, setWingList] = useState([]);
  const [CircleList, setCircleList] = useSessionArray("masterCircleList");
  const [DivisionList, setDivisionList] = useSessionArray("masterDivisionList");
  const [SubDivisionList, setSubdivisionList] = useSessionArray(
    "masterSubDivisionList",
  );
  const [AreaList, setAreaList] = useSessionArray("masterAreaList");
  const [projectnames, setProjectnames] = useSessionArray("masterProjectNames");

  const [isLoading, setisLoading] = useState(
    () => sessionStorage.getItem("masterisloading") !== "false",
  );
  useEffect(() => {
    sessionStorage.setItem("masterisloading", isLoading);
  }, [isLoading]);

  const [totalpageno, setTotalpageno] = useState(0);

  // ---- selections (persisted per session) ----
  const [selectedWing, setSelectedWing] = useSessionArray("masterselectedWing");
  const [selectedCircle, setSelectedCircle] = useSessionArray(
    "masterselectedCircle",
  );
  const [selectedDivision, setSelectedDivision] = useSessionArray(
    "masterselectedDivision",
  );
  const [selectedsubDivision, setSelectedSubDivision] = useSessionArray(
    "masterselectedsubDivision",
  );
  const [selectedarea, setSelectedArea] = useSessionArray("masterselectedarea");
  const [selectedprojectnam, setSelectedProjectnam] = useSessionArray(
    "masterselectedprojectnam",
  );

  const selectedByType = {
    wing: selectedWing,
    circle: selectedCircle,
    division: selectedDivision,
    sub_division: selectedsubDivision,
    area: selectedarea,
    project_name: selectedprojectnam,
  };
  const settersByType = {
    wing: setSelectedWing,
    circle: setSelectedCircle,
    division: setSelectedDivision,
    sub_division: setSelectedSubDivision,
    area: setSelectedArea,
    project_name: setSelectedProjectnam,
  };
  const childSetters = {
    wing: setCircleList,
    circle: setDivisionList,
    division: setSubdivisionList,
    sub_division: setAreaList,
  };

  // ---- UI state ----
  const [showFilters, setShowFilters] = useState(false); // mobile / tablet drawer
  const [openDropdown, setOpenDropdown] = useState(null); // one open at a time
  const [search, setSearch] = useState({});
  const [lgShow, setLgShow] = useState(false);
  const [userlistdata, setUserlistdata] = useState([]);
  const fileInputRef = useRef(null); // used by the mobile "Upload File" menu item

  const [successstatus, setSuccessstatus] = useState(false);
  const [successtoastmsg, setSuccesstoastmsg] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [messageList, setMessageList] = useState("");
  const [tokenmsg, setTokenmsg] = useState("");
  const [isUploadingHierarchy, setIsUploadingHierarchy] = useState(false);

  const setSearchFor = (key, value) =>
    setSearch((prev) => ({ ...prev, [key]: value }));

  // ---- API helpers ----
  const authHeaders = {
    "Content-Type": "application/json",
    Authorization: "Bearer " + userData.token,
  };

  const fetchProjects = (filters, pgNo = 1) =>
    axios({
      method: "POST",
      url: `${baseurl}/api/project/get/project-details?userId=${userData.userId}&pgNo=${pgNo}&isSortingRequired=1`,
      headers: authHeaders,
      data: filters,
    });

  const listRequest = (params) =>
    axios({
      method: "GET",
      url: `${baseurl}/api/get/list?userId=${userData.userId}&isSortingRequired=2`,
      params,
      headers: authHeaders,
    });

  // Every selected level, plus project name.
  const currentFilters = () => {
    const filters = {};
    Object.keys(ID_KEY).forEach((t) => {
      if (selectedByType[t].length)
        filters[ID_KEY[t]] = [selectedByType[t][0].id];
    });
    if (selectedprojectnam.length) {
      filters.projectName = [selectedprojectnam[0].projectName];
    }
    return filters;
  };

  // Selected levels ABOVE `type`, plus the newly picked option for `type`.
  const filtersUpTo = (type, option) => {
    const filters = {};
    for (const t of Object.keys(ID_KEY)) {
      if (t === type) {
        filters[ID_KEY[t]] = [option.id];
        break;
      }
      if (selectedByType[t].length)
        filters[ID_KEY[t]] = [selectedByType[t][0].id];
    }
    return filters;
  };

  const applyProjectResponse = (res) => {
    setMasterTab1(res.data?.projectDetails || []);
    setTotalpageno(res.data?.totalPages || 0);
    setisLoading(false);
  };

  // ---- data loading ----
  const handleMasterData = () => {
    setisLoading(true);
    fetchProjects()
      .then((res) => {
        applyProjectResponse(res);
      })
      .catch((err) => {
        console.error(err);
        setisLoading(false);
      });
  };

  const handlePageClick = ({ selected }) => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    setisLoading(true);
    fetchProjects(currentFilters(), selected + 1)
      .then((res) => {
        setMasterTab1(res.data?.projectDetails || []);
        setisLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setisLoading(false);
      });
  };

  // First load: only fetch everything when no filter was restored from session.
  useEffect(() => {
    const hasFilter = Object.values(selectedByType).some((a) => a.length > 0);
    if (!hasFilter) {
      setMasterTab1([]);
      handleMasterData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Wing list
  const fetchWingList = () => {
    listRequest({ field: "wing" })
      .then((res) => setWingList(res.data?.message || []))
      .catch((error) => {
        const msg = error.response?.data?.message;
        if (msg !== undefined) setTokenmsg(msg);
      });
  };

  useEffect(() => {
    fetchWingList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // DE users: lock wing, circle + division to their own and preload sub-divisions.
  useEffect(() => {
    if (!isDE || !WingList?.length) return;

    const userWing = WingList.find(
      (i) =>
        Number(i.id) === Number(userData.wing) ||
        (userData.wingName && i.name === userData.wingName),
    );
    if (!userWing) return;
    setSelectedWing([userWing]);

    listRequest({ field: "circle", wingId: userWing.id })
      .then((res) => {
        const circleData = res.data.message || [];
        setCircleList(circleData);

        const userCircle = circleData.find(
          (i) =>
            Number(i.id) === Number(userData.circle) ||
            (userData.circleName && i.name === userData.circleName),
        );
        if (!userCircle) return;
        setSelectedCircle([userCircle]);

        return listRequest({ field: "division", circleId: userCircle.id });
      })
      .then((res) => {
        if (!res) return;
        const divisionData = res.data.message || [];
        setDivisionList(divisionData);

        const userDivision = divisionData.find(
          (i) =>
            Number(i.id) === Number(userData.division) ||
            (userData.divisionName && i.name === userData.divisionName),
        );
        if (!userDivision) return;
        setSelectedDivision([userDivision]);

        return listRequest({
          field: "subDivision",
          divisionId: userDivision.id,
        });
      })
      .then((res) => {
        if (res) setSubdivisionList(res.data.message || []);
      })
      .catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    userData?.role,
    userData?.wing,
    userData?.wingName,
    userData?.circle,
    userData?.circleName,
    userData?.division,
    userData?.divisionName,
    userData?.userId,
    userData?.token,
    WingList,
  ]);

  // ---- filter interactions ----
  const handleSelectOption = (type, option) => {
    const index = FILTER_ORDER.indexOf(type);

    settersByType[type]([option]);
    FILTER_ORDER.slice(index + 1).forEach((t) => settersByType[t]([]));
    setOpenDropdown(null);
    setMasterTab1([]);

    const filters =
      type === "project_name"
        ? { ...currentFilters(), projectName: [option.projectName] }
        : filtersUpTo(type, option);

    setisLoading(true);
    fetchProjects(filters)
      .then(applyProjectResponse)
      .catch((err) => {
        console.error(err);
        setisLoading(false);
      });

    if (CHILD_LIST[type]) {
      const { field, param } = CHILD_LIST[type];
      listRequest({ field, [param]: option.id })
        .then((res) => childSetters[type](res.data.message))
        .catch(console.error);
    }

    if (type === "area") {
      const params = { field: "projectName", areaId: option.id };
      ["wing", "circle", "division", "sub_division"].forEach((t) => {
        if (selectedByType[t].length)
          params[ID_KEY[t]] = selectedByType[t][0].id;
      });
      listRequest(params)
        .then((res) => setProjectnames(res.data.message))
        .catch(console.error);
    }
  };

  const filterFields = [
    {
      key: "wing",
      label: "Wing",
      list: WingList,
      selected: selectedWing,
      nameKey: "name",
      locked: isDE,
      disabled: isDE,
    },
    {
      key: "circle",
      label: "Circle",
      list: CircleList,
      selected: selectedCircle,
      nameKey: "name",
      locked: isDE,
      disabled: isDE || !selectedWing.length,
    },
    {
      key: "division",
      label: "Division",
      list: DivisionList,
      selected: selectedDivision,
      nameKey: "name",
      locked: isDE,
      disabled: isDE || !selectedCircle.length,
    },
    {
      key: "sub_division",
      label: "Sub Division",
      list: SubDivisionList,
      selected: selectedsubDivision,
      nameKey: "name",
      disabled: !selectedDivision.length,
    },
    {
      key: "area",
      label: "Area",
      list: AreaList,
      selected: selectedarea,
      nameKey: "name",
      disabled: !selectedsubDivision.length,
    },
    {
      key: "project_name",
      label: "Project Name",
      list: projectnames,
      selected: selectedprojectnam,
      nameKey: "projectName",
      disabled: !selectedarea.length,
      dropUp: true,
    },
  ];

  const handleToggle = (key) => {
    const field = filterFields.find((f) => f.key === key);
    if (!field || field.disabled || field.locked) return;
    setSearchFor(key, "");
    setOpenDropdown((prev) => {
      if (field.locked) return null;
      return prev === key ? null : key;
    });
  };

  const handleReset = () => {
    handleMasterData();
    setSearch({});

    if (!isDE) {
      setSelectedWing([]);
      setSelectedCircle([]);
      setSelectedDivision([]);
    }
    setSelectedSubDivision([]);
    setSelectedArea([]);
    setSelectedProjectnam([]);
    setOpenDropdown(null);
  };

  const activeFilterCount = Object.values(selectedByType).filter(
    (a) => a.length > 0,
  ).length;

  // Close an open dropdown when the user taps outside any filter field.
  useEffect(() => {
    if (!openDropdown) return;
    const onPointerDown = (e) => {
      if (!e.target.closest?.(".filter-field")) setOpenDropdown(null);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [openDropdown]);

  // Drawer: lock page scroll, close on Escape, close when the sidebar takes over.
  useEffect(() => {
    if (!showFilters) return;
    const onKey = (e) => e.key === "Escape" && setShowFilters(false);
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener("keydown", onKey);
    };
  }, [showFilters]);

  useEffect(() => {
    if (isDesktop) setShowFilters(false);
  }, [isDesktop]);

  // ---- other actions ----
  const handleuserlist = (users) => {
    setUserlistdata(users);
    setLgShow(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    e.target.value = ""; // allow picking the same file again

    setIsUploadingHierarchy(true);

    axios({
      method: "POST",
      url:
        baseurl +
        "/api/import-data-from-excel?userId=" +
        userData.userId,
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${userData.token}`,
      },
      data: formData,
    })
      .then((response) => {
        setSuccessstatus(true);
        setSuccesstoastmsg(response.data.message);
        fetchWingList();
      })
      .catch((error) => {
        setShowToast(true);
        setMessageList(error.response?.data?.message || "Upload failed");
      })
      .finally(() => {
        setIsUploadingHierarchy(false);
      });
  };

  const handleExportToExcel = () => {
    axios({
      method: "POST",
      url:
        baseurl +
        "/api/project/export/project-details?userId=" +
        userData.userId,
      headers: authHeaders,
      responseType: "blob",
    })
      .then((response) => {
        let filename = "project_details.xlsx";
        const contentDisposition = response.headers["content-disposition"];
        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?([^"]+)"?/);
          if (match?.[1]) filename = match[1];
        }

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(console.error);
  };

  const ToclosesuccessToast = () => {
    setSuccesstoastmsg("");
    setSuccessstatus(false);
  };

  const TocloseToast1 = () => {
    setMessageList("");
    setShowToast(false);
  };

  /* ------------------------------------------------------------------------ */
  /* Render                                                                   */
  /* ------------------------------------------------------------------------ */

  // Called as a function (not used as <Component/>) so inputs keep focus.
  const renderFilterPanel = ({ drawer = false } = {}) => (
    <>
      {!drawer && (
        <div className="p-2 d-flex justify-content-between align-items-center text-white longhand ">
          <p className="mb-0">Filter</p>
          <button
            type="button"
            className="mb-0 border px-2 pointerborder"
            onClick={handleReset}
          >
            Remove
          </button>
        </div>
      )}
      <div className="px-2 pb-2 pt-0 border">
        {filterFields.map((field) => (
          <FilterSelect
            key={field.key}
            field={field}
            dropUp={!drawer && field.dropUp}
            isOpen={openDropdown === field.key}
            search={search[field.key]}
            onToggle={handleToggle}
            onSearch={setSearchFor}
            onSelect={handleSelectOption}
          />
        ))}
      </div>
    </>
  );

  return (
    <>
      <LogoutComponent tokenExpirationMsg={tokenmsg} />
      {isUploadingHierarchy &&
        (typeof document !== "undefined"
          ? createPortal(
              <div className="overlay-loading-div2 d-flex justify-content-center align-items-center">
                <ThreeDots
                  visible={true}
                  height="50"
                  width="100"
                  color="#3369a6"
                  radius="7"
                  ariaLabel="three-dots-loading"
                />
              </div>,
              document.body,
            )
          : (
              <div className="overlay-loading-div2 d-flex justify-content-center align-items-center">
                <ThreeDots
                  visible={true}
                  height="50"
                  width="100"
                  color="#3369a6"
                  radius="7"
                  ariaLabel="three-dots-loading"
                />
              </div>
            ))}

      {successstatus && (
        <CustomSuccessToastContainer
          message={successtoastmsg}
          onClose={ToclosesuccessToast}
          show={successstatus}
        />
      )}

      {showToast && (
        <CustomErrorToastContainer
          message={messageList}
          onClose={TocloseToast1}
          show={showToast}
        />
      )}

      <div className="mx-md-4 core-zoom">
        {/* Toolbar */}
        <div className="master-toolbar mb-2">
          {/* Filter trigger (below lg) */}
          <button
            type="button"
            className="btn shadow text-white d-inline-flex align-items-center custom-btn-user-style d-lg-none master-filter-trigger w-auto"
            onClick={() => setShowFilters(true)}
            aria-label="Open filters"
          >
            <RiFilter2Fill className="font20" />
            <span className="d-none d-sm-inline">Filter</span>
            {activeFilterCount > 0 && (
              <span className="master-filter-count">{activeFilterCount}</span>
            )}
          </button>

          {/* md and up: action buttons */}
          <div className="master-actions d-none d-md-flex">
            <a
              href="/Hierachy Template.xlsx"
              download
              className="text-white no-underline"
            >
              <button
                type="button"
                title="Download Template"
                aria-label="Download Template"
                className="btn shadow text-white d-inline-flex align-items-center custom-btn-user-style w-auto"
              >
                <HiOutlineDownload className="font20" />
                <span className="d-none d-md-block">Hierarchy Template</span>
              </button>
            </a>

            <button
              onClick={handleExportToExcel}
              type="button"
              title="Export Data"
              aria-label="Export Data"
              className="btn shadow text-white d-inline-flex align-items-center custom-btn-user-style w-auto"
            >
              <TbFileExport className="font20" />
              <span className="d-none d-md-block">Export</span>
            </button>

            <button
              type="button"
              title="Upload File"
              aria-label="Upload File"
              className="container-btn-file font12"
            >
              <ExcelIcon />
              <span className="d-none d-md-block">Hierarchy Upload</span>
              <input
                className="file"
                name="text"
                type="file"
                onChange={handleFileChange}
              />
            </button>
          </div>

          {/* Below md: the same actions in one dropdown */}
          <Dropdown align="end" className="d-md-none ms-auto">
            <Dropdown.Toggle
              as="button"
              bsPrefix="master-actions-toggle"
              type="button"
              aria-label="More actions"
              className="btn shadow text-white d-inline-flex align-items-center justify-content-center custom-btn-user-style w-auto"
            >
              Actions
            </Dropdown.Toggle>

            <Dropdown.Menu className="master-actions-menu">
              <Dropdown.Item
                as="a"
                href="/Hierarchy Template.xlsx"
                download
                className="master-actions-item"
              >
                <HiOutlineDownload size={18} />Hierarchy Template
              </Dropdown.Item>

              <Dropdown.Item
                as="button"
                type="button"
                onClick={handleExportToExcel}
                className="master-actions-item"
              >
                <TbFileExport size={18} /> Export
              </Dropdown.Item>

              <Dropdown.Divider />

              <Dropdown.Item
                as="button"
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="master-actions-item"
              >
                <ExcelIcon fill="currentColor" size={16} /> Hierarchy Upload
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>

          {/* Always mounted so the file picker still works after the menu closes */}
          <input
            ref={fileInputRef}
            type="file"
            className="d-none"
            onChange={handleFileChange}
          />
        </div>

        <div className="row">
          {/* Sidebar filters (large screens) */}
          <div className="col-lg-3 d-none d-lg-block">
            {renderFilterPanel()}
          </div>

          {/* Table */}
          <div className="col-12 col-lg-9">
            <div className="table-responsive master-table-container">
              <table className="w-100 table table-bordered stack-table fsi-12">
                <thead className="textback fsi-12">
                  <tr className="fsi-12">
                    <th className="borrad">Wing</th>
                    <th>Circle</th>
                    <th>Division</th>
                    <th>Subdivision</th>
                    <th>Area</th>
                    <th className="text-nowrap">Project Name</th>
                    <th className="borrad">Userlist</th>
                  </tr>
                </thead>
                <tbody className="center-aligned-content">
                  {!isLoading && (!masterTab1 || masterTab1.length === 0) ? (
                    <tr>
                      <td
                        colSpan={7}
                        className="text-center py-4 text-muted fw-bold"
                      >
                        No data found
                      </td>
                    </tr>
                  ) : (
                    masterTab1?.map((item, index) => (
                    <tr key={`${item.projectName}-${index}`}>
                      <td data-label="Wing" className="text-nowrap">
                        {item.wing}
                      </td>
                      <td data-label="Circle" className="text-nowrap">
                        {item.circle}
                      </td>
                      <td data-label="Division" className="text-nowrap">
                        {item.division}
                      </td>
                      <td data-label="Subdivision" className="text-nowrap">
                        {item.subDivision}
                      </td>
                      <td data-label="Area" className="text-nowrap">
                        {item.area}
                      </td>
                      <td data-label="Project Name" className="text-nowrap">
                        <button
                          type="button"
                          className="w-250px text-nowrap text-truncate mx-auto project-cell p-0 btn btn-sm"
                          title={item.projectName}
                        >
                          {item.projectName}
                        </button>
                      </td>
                      <td data-label="Userlist" className="text-nowrap">
                        {item.userList?.length ? (
                          <button
                            className="btn-view-cart"
                            type="button"
                            onClick={() => handleuserlist(item.userList)}
                          >
                            View
                          </button>
                        ) : (
                          "NA"
                        )}
                      </td>
                    </tr>
                  )))}
                </tbody>
              </table>
            </div>

            <div className="d-flex w-100 justify-content-center justify-content-md-end mt-2">
              {masterTab1 && masterTab1.length !== 0 && (
                <ReactPaginate
                  breakLabel="..."
                  nextLabel={<GrFormNext />}
                  onPageChange={handlePageClick}
                  pageRangeDisplayed={1}
                  marginPagesDisplayed={isPhone ? 1 : 2}
                  pageCount={totalpageno}
                  previousLabel={<GrFormPrevious />}
                  renderOnZeroPageCount={null}
                  containerClassName="pagination m-0 flex-nowrap align-items-center"
                  activeClassName="active"
                />
              )}
            </div>

            {isLoading && (
              <div className="d-flex justify-content-center w-100 mt-5">
                <ThreeDots
                  visible={true}
                  height="20"
                  width="40"
                  color="#3369a6"
                  radius="7"
                  ariaLabel="three-dots-loading"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filter drawer (below lg) */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            key="filter-backdrop"
            className="filter-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setShowFilters(false)}
          />
        )}
        {showFilters && (
          <motion.aside
            key="filter-drawer"
            className="filter-drawer"
            role="dialog"
            aria-modal="true"
            aria-label="Filters"
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{ type: "tween", duration: 0.25 }}
          >
            <div className="filter-drawer-header">
              <strong>Filter</strong>
              <div className="d-flex align-items-center gap-2">
                <button
                  className="border rounded px-2 pointerborder"
                  onClick={handleReset}
                >
                  Remove
                </button>
                <button
                  type="button"
                  className="btn px-2 py-0"
                  aria-label="Close filters"
                  onClick={() => setShowFilters(false)}
                >
                  <HiXMark size={20} />
                </button>
              </div>
            </div>

            <div className="filter-drawer-body">
              {renderFilterPanel({ drawer: true })}
            </div>

          </motion.aside>
        )}
      </AnimatePresence>

      {/* User list modal */}
      <Modal centered size="xl" show={lgShow} onHide={() => setLgShow(false)}>
        <Modal.Body className="bg-white">
          <div className="d-flex justify-content-end align-items-center w-100 p-1 pb-2">
            <button
              onClick={() => setLgShow(false)}
              className="btn px-2 py-0"
              aria-label="Close"
            >
              <HiXMark size={20} />
            </button>
          </div>
          <hr className="my-2" />
          <div className="table-responsive">
            <table className="w-100 table table-bordered stack-table fsi-12">
              <thead className="verticalelement fsi-12">
                <tr className="fsi-12">
                  <th className="center-aligned-content text-nowrap rounded-0">
                    Name
                  </th>
                  <th className="center-aligned-content text-nowrap">Role</th>
                  <th className="center-aligned-content text-nowrap">Email</th>
                  <th className="center-aligned-content text-nowrap">Phone</th>
                  <th className="center-aligned-content text-nowrap">
                    User Access
                  </th>
                  <th className="center-aligned-content text-nowrap">
                    Report To
                  </th>
                  <th className="center-aligned-content text-nowrap">
                    Reported By
                  </th>
                  <th className="center-aligned-content text-nowrap rounded-0">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="center-aligned-content">
                {!userlistdata || userlistdata.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="text-center py-4 text-muted fw-bold"
                    >
                      No data found
                    </td>
                  </tr>
                ) : (
                  userlistdata?.map((item, index) => (
                  <tr key={`${item.name}-${index}`}>
                    <td data-label="Name" className="fsi-12">
                      {item.name}
                    </td>
                    <td data-label="Role" className="fsi-12">
                      {item.role}
                    </td>
                    <td data-label="Email" className="fsi-12">
                      {item.email}
                    </td>
                    <td data-label="Phone" className="fsi-12">
                      {item.contactNumber}
                    </td>
                    <td data-label="User Access" className="fsi-12">
                      {item.role}
                    </td>
                    <td data-label="Report To" className="fsi-12">
                      {item.reportTo}
                    </td>
                    <td data-label="Reported By" className="fsi-12">
                      {item.reportBy}
                    </td>
                    <td data-label="Status" className="fsi-12">
                      {item.activeStatus && item.activeStatus == 1
                        ? "Active"
                        : "Inactive"}
                    </td>
                  </tr>
                )))}
              </tbody>
            </table>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}
